import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { Dashboard, Metric, ModuleData, VideoBlock } from '@pixerito/api-client';
import { formatCompact, formatDate, formatInt, formatValue } from '../lib/format';
import { dashboardRange, previousRange, rangeLong, rangeName, rangeShort, type PeriodRange } from '../lib/period';
import { DeliverableCard } from './DeliverableCard';
import { LineChart } from './LineChart';
import { ExportMenu } from './ExportMenu';
import { PeriodPicker } from './PeriodPicker';
import { Delta, DeltaValue, Empty, Watermark } from './ui';

interface Props {
  data: Dashboard;
  onRangeChange: (range: PeriodRange) => void;
  deliverablesHref?: string;
  greeting?: string;
}

/** O dashboard do cliente. Mesma tela para o cliente e para a prévia do admin. */
export function DashboardView({ data, onRangeChange, deliverablesHref, greeting }: Props) {
  const periods = data.availablePeriods; // mais recente primeiro
  const range = dashboardRange(data);
  const previous = previousRange(data);
  const multi = range !== null && range.from !== range.to; // vários meses (trimestre, semestre, ano...)
  const periodName = range ? rangeName(range) : null;
  const compareLabel = previous ? rangeShort(previous) : null;

  const metricModules = data.modules.filter((m) => m.kind === 'metrics');
  const deliverableModules = data.modules.filter((m) => m.kind === 'deliverables');

  return (
    <div className="stack-lg">
      <section className="dash-hero">
        <Watermark className="dash-hero__wm" />
        <div className="stack" style={{ gap: 8, minWidth: 0 }}>
          <p className="eyebrow">&lt;_{data.client.name.replace(/\s+/g, '_')}/&gt;</p>
          <h1 className="t-display">
            {greeting ? (
              <>
                {greeting}
                <br />
              </>
            ) : null}
            {range ? (
              <>
                Resultados de <span className="hl">{rangeName(range)}</span>
              </>
            ) : (
              'Seu painel Pixerito'
            )}
          </h1>
          <p className="t-sub">
            {range && previous
              ? `${rangeLong(range)} comparado com ${rangeLong(previous)}`
              : 'Os números do mês aparecem aqui assim que a equipe fizer o primeiro lançamento.'}
          </p>
        </div>

        <div className="dash-hero__tools">
          {range && <PeriodPicker value={range} available={periods} onChange={onRangeChange} />}
          <ExportMenu data={data} />
        </div>
      </section>

      {(metricModules.length > 0 || deliverableModules.length > 0) && (
        <nav className="module-index" aria-label="Serviços contratados">
          {data.modules.map((m) => (
            <a key={m.type} href={`#modulo-${m.type}`} className="module-index__item">
              <span className="module-index__name">{m.label}</span>
              <span className="module-index__meta">{headline(m, multi)}</span>
            </a>
          ))}
        </nav>
      )}

      {data.modules.length === 0 && <Empty title="Nenhum serviço ativo">A equipe Pixerito ainda não ativou os módulos da sua conta.</Empty>}

      {metricModules.map((m) => (
        <MetricModule key={m.type} module={m} periodName={periodName} compareLabel={compareLabel} multi={multi} />
      ))}
      {deliverableModules.map((m) => (
        <DeliverableModule
          key={m.type}
          module={m}
          periodName={periodName}
          compareLabel={compareLabel}
          multi={multi}
          href={deliverablesHref}
        />
      ))}
      <p className="print-only t-caption">Relatório gerado em {new Date().toLocaleDateString('pt-BR')} · Pixerito Social</p>
    </div>
  );
}

function headline(m: ModuleData, multi: boolean) {
  if (m.kind === 'deliverables') {
    const open = m.deliverables.filter((d) => d.status !== 'delivered').length;
    if (m.type === 'video' && m.videos?.isRecurring && m.videos.current.count > 0) {
      return `${m.videos.current.count} vídeos · ${formatCompact(m.videos.current.views)} views`;
    }
    return m.deliverables.length === 0 ? 'nada em andamento' : open > 0 ? `${open} em andamento` : 'tudo entregue';
  }
  const lead = m.metrics.find((x) => x.chartable) ?? m.metrics[0];
  if (!lead || lead.current === null) return multi ? 'sem dados no período' : 'sem dados no mês';
  return `${lead.label}: ${formatValue(lead.current, lead.unit)}`;
}

// ── Módulos de métrica ──────────────────────────────────────────

interface PeriodProps {
  /** "agosto", "julho a setembro"... Nulo quando o cliente ainda não tem nenhum mês. */
  periodName: string | null;
  /** Rótulo curto do período anterior, ao lado da comparação ("jul/26", "abr–jun/26"). */
  compareLabel: string | null;
  multi: boolean;
}

function MetricModule({ module: m, periodName, compareLabel, multi }: { module: ModuleData } & PeriodProps) {
  const tiles = m.metrics.filter((x) => x.group === null);
  const groups = useMemo(() => {
    const map = new Map<string, Metric[]>();
    m.metrics.filter((x) => x.group).forEach((x) => map.set(x.group!, [...(map.get(x.group!) ?? []), x]));
    return [...map.entries()];
  }, [m.metrics]);
  const chartable = m.metrics.filter((x) => x.chartable);
  const [chartKey, setChartKey] = useState(chartable[0]?.key);
  const chartMetric = chartable.find((x) => x.key === chartKey) ?? chartable[0];
  const series = m.series && chartMetric ? m.series.values[chartMetric.key] : null;
  const pointsWithData = series?.filter((v) => v !== null).length ?? 0;

  return (
    <section className="module" id={`modulo-${m.type}`} aria-labelledby={`t-${m.type}`}>
      <header className="module__head">
        <h2 className="t-h2" id={`t-${m.type}`}>
          {m.label}
        </h2>
        {m.updatedAt && <span className="t-caption">Atualizado em {formatDate(m.updatedAt)}</span>}
      </header>

      {!m.hasData ? (
        <Empty title={`Sem números de ${periodName ?? 'este mês'}`}>
          Assim que a equipe lançar os dados {multi ? 'deste período' : 'deste mês'}, eles aparecem aqui com a comparação.
        </Empty>
      ) : (
        <>
          <div className="kpis">
            {tiles.map((x) => (
              <div key={x.key} className="kpi">
                <span className="kpi__label">{x.label}</span>
                <span className="kpi__value num">{formatValue(x.current, x.unit)}</span>
                <span className="kpi__foot">
                  <Delta metric={x} noneLabel={multi ? 'sem período anterior' : undefined} />
                  {x.previous !== null && compareLabel && (
                    <span className="t-caption">
                      {compareLabel}: {formatValue(x.previous, x.unit)}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {groups.length > 0 && (
            <div className="breakdowns">
              {groups.map(([name, items]) => (
                <Breakdown key={name} name={name} items={items} />
              ))}
            </div>
          )}

          {chartMetric && series && m.series && (
            <div className="card chart-card">
              <div className="card__head">
                <div>
                  <p className="label">Evolução mês a mês</p>
                  <p className="t-h3">{chartMetric.label}</p>
                </div>
                {chartable.length > 1 && (
                  <div className="tabs" role="tablist" aria-label="Métrica do gráfico">
                    {chartable.map((x) => (
                      <button key={x.key} role="tab" aria-selected={x.key === chartMetric.key} onClick={() => setChartKey(x.key)}>
                        {x.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {pointsWithData >= 2 ? (
                <LineChart periods={m.series.periods} values={series} unit={chartMetric.unit} label={chartMetric.label} />
              ) : (
                <p className="muted t-small" style={{ padding: '24px 0' }}>
                  O gráfico aparece a partir do segundo mês com dados.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Breakdown({ name, items }: { name: string; items: Metric[] }) {
  const total = items.reduce((s, x) => s + (x.current ?? 0), 0);
  const max = Math.max(1, ...items.map((x) => x.current ?? 0));
  return (
    <div className="card breakdown">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <p className="label">{name}</p>
        <span className="t-caption num">total {formatInt(total)}</span>
      </div>
      <ul className="stack" style={{ gap: 12 }}>
        {items.map((x) => {
          const share = total > 0 && x.current !== null ? Math.round((x.current / total) * 100) : 0;
          return (
            <li key={x.key} className="bar">
              <div className="bar__top">
                <span className="t-small" style={{ fontWeight: 500 }}>
                  {x.label}
                </span>
                <span className="bar__nums">
                  <span className="num t-small" style={{ fontWeight: 600 }}>
                    {formatValue(x.current, x.unit)}
                  </span>
                  <span className="t-caption num">{share}%</span>
                  <Delta metric={x} compact />
                </span>
              </div>
              <div className="bar__track" aria-hidden="true">
                <div className="bar__fill" style={{ width: `${((x.current ?? 0) / max) * 100}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Identidade Visual / Vídeo ───────────────────────────────────

function DeliverableModule({ module: m, periodName, compareLabel, multi, href }: { module: ModuleData; href?: string } & PeriodProps) {
  const open = m.deliverables.filter((d) => d.status !== 'delivered');
  const done = m.deliverables.filter((d) => d.status === 'delivered');
  const shown = [...open, ...done].slice(0, 3);

  return (
    <section className="module" id={`modulo-${m.type}`} aria-labelledby={`t-${m.type}`}>
      <header className="module__head">
        <h2 className="t-h2" id={`t-${m.type}`}>
          {m.label}
        </h2>
        <span className="t-caption">
          {open.length} em andamento · {done.length} entregue{done.length === 1 ? '' : 's'}
        </span>
      </header>

      {m.type === 'video' && m.videos?.isRecurring && (
        <VideoMonth block={m.videos} periodName={periodName} compareLabel={compareLabel} multi={multi} />
      )}

      {m.deliverables.length === 0 ? (
        <Empty title="Nenhum entregável ainda">Quando a produção começar, você acompanha cada etapa por aqui.</Empty>
      ) : (
        <div className="stack">
          {m.type === 'video' && m.videos?.isRecurring && <p className="label">Produção</p>}
          <div className="deliv-grid">
            {shown.map((d) => (
              <DeliverableCard key={d.id} deliverable={d} showModule={false} />
            ))}
          </div>
          {href && m.deliverables.length > shown.length && (
            <div>
              <Link to={href} className="btn btn--ghost btn--sm">
                Ver todos os {m.deliverables.length} entregáveis
              </Link>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function VideoMonth({ block, periodName, compareLabel, multi }: { block: VideoBlock } & PeriodProps) {
  const cur = block.current;
  const prev = block.previous;
  const stats: [string, number, number | null | undefined][] = [
    ['Vídeos publicados', cur.count, prev?.count ?? null],
    ['Visualizações', cur.views, prev?.views],
    ['Curtidas', cur.likes, prev?.likes],
    ['Comentários', cur.comments, prev?.comments],
    ['Compartilhamentos', cur.shares, prev?.shares],
  ];

  if (block.items.length === 0) {
    return (
      <Empty title={`Nenhum vídeo lançado em ${periodName ?? 'este mês'}`}>
        Os vídeos publicados {multi ? 'no período' : 'no mês'} aparecem aqui com visualizações, curtidas, comentários e compartilhamentos.
      </Empty>
    );
  }

  return (
    <div className="stack">
      <div className="kpis kpis--5">
        {stats.map(([label, value, before]) => (
          <div key={label} className="kpi">
            <span className="kpi__label">{label}</span>
            <span className="kpi__value num">{formatInt(value)}</span>
            <span className="kpi__foot">
              <DeltaValue current={value} previous={before ?? null} />
              {before !== null && before !== undefined && compareLabel && (
                <span className="t-caption">
                  {compareLabel}: {formatInt(before)}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
      <div className="card card--flush">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>
                  {multi ? 'Vídeos' : 'Vídeo'} de {periodName ?? ''}
                </th>
                <th className="r">Views</th>
                <th className="r">Curtidas</th>
                <th className="r">Coment.</th>
                <th className="r">Compart.</th>
              </tr>
            </thead>
            <tbody>
              {block.items.map((v) => (
                <tr key={v.id}>
                  <td>
                    <div className="stack" style={{ gap: 0 }}>
                      {v.url ? (
                        <a href={v.url} target="_blank" rel="noreferrer" className="video-link">
                          {v.title}
                        </a>
                      ) : (
                        <span>{v.title}</span>
                      )}
                      <span className="t-caption">
                        {v.platform}
                        {v.publishedOn ? ` · ${formatDate(v.publishedOn)}` : ''}
                      </span>
                    </div>
                  </td>
                  <td className="r num">{formatInt(v.views)}</td>
                  <td className="r num">{formatInt(v.likes)}</td>
                  <td className="r num">{formatInt(v.comments)}</td>
                  <td className="r num">{formatInt(v.shares)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
